import React from 'react';
import { useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { css } from '@emotion/react';

import { Breadcrumbs, Grid, StructuredData } from '@gumtree/ui-library';
import PartnershipAdSlot from '@gumtree/third-party-ads/src/framework/partnership/partnership-ad-slot';
import AdSlot from '@gumtree/third-party-ads/display-ad-slot';
import {  DisplayAdSlotDivNames } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import DetailExpandableList from '@gumtree/ui-library/src/detail-expandable-list/detail-expandable-list';
import { DetailExpandableItemProps } from '@gumtree/ui-library/src/detail-expandable-item/detail-expandable-item';
import RequestAQuoteForm from '@gumtree/srp/src/components/request-a-quote-form';
import HeaderBlock from './components/header-block/index';
import StaySafe from './components/stay-safe';
import SimilarAdsCarousel from './components/similar-ads-carousel';
import SharePanel from './components/share-panel/container';
import Description from './components/description/index';
import Attributes from './components/attributes/index';
import NewContactCard from './components/new-contact-card';
import Disclaimer from './components/disclaimer';
import SeoL1Category from './components/seo-l1-category';
import StickyHeader from './components/sticky-header';
import StickyTabNavigation  from './components/sticky-tab-navigation/StickyTabNavigation';
import Overview  from './components/overview/Overview';
import VHCPlaceholder from './components/vhc/vhc-placeholder';
import VHCPanel from './components/vhc/panel';
import Portfolio from './components/portfolio';
import VipSkills from './components/vip-skills';

import './app.scss';
import {
    AdBreadcrumbSlot,
    CompactPartnershipAdsContainer,
    RightPanel,
    SimilarAdsCarouselContainer,
    SmallDesktopVhc,
    SocialPanelLeft,
    SocialPanelRight,
    StaySafeContainer,
    VhcPanelContainer,
    VipAdInfo,
    VipAdSocial,
    VipAdTitle,
    VipCarouselContainer,
    VipContainer,
    VipContent,
    SlotsWrapper,
    BreadcrumbsWrapper,
} from './app.style';
import { getPopulatedSchema } from './seo-utils';
import { selectState, selectPartnershipParams } from './selectors';
import type { VipState } from './reducers';
import { MotorsCTAContext, RequestAQuoteFormContext } from './vip-context';
import FavouriteButton from './components/seller-actions-block/favourite';
import ReportButton from './components/seller-actions-block/report';
import { renderAdSlot, usePartnershipAdFlags } from './utils/adUtils';
import { AdReviews } from './types/ad-reviews';
import AdReviewsList from './components/ad-reviews-list';

const queryClient = new QueryClient();

export const renderVipAdInfo = ({
    isMobile,
    partnershipParams,
    adDetails,
    baseConfig,
    userData,
    sellerContactDetails,
    isPartnerAdDefault1Gam,
    sellerSkills,
    sellerSkillsList,
    tabs,
    overview,
    images,
    // adSource,
    scoreAndReviews,
    adReviews,
    qaPairs,
    otherServices,
    requestId,
    isRequestAQuoteFormOpen,
    setIsRequestAQuoteFormOpen,
    requestAQuotePreferredContactType,
}: {
    isMobile: boolean;
    partnershipParams: any;
    adDetails: VipState['adDetails'];
    baseConfig: VipState['baseConfig'];
    userData: any;
    sellerContactDetails: any;
    isPartnerAdDefault1Gam: boolean;
    sellerSkills: any;
    sellerSkillsList: any;
    tabs: any;
    overview: any;
    images: any;
    adSource?: string;
    adReviews?: AdReviews;
    scoreAndReviews: {
        average: number;
        total: number;
        topTips: string;
        bottomTips: string;
    };
    qaPairs?: DetailExpandableItemProps[];
    otherServices?: DetailExpandableItemProps[];
    requestId?: string;
    isRequestAQuoteFormOpen: boolean;
    setIsRequestAQuoteFormOpen: (open: boolean) => void;
    requestAQuotePreferredContactType: 'call' | 'quote';
}) => (
    <VipAdInfo className="grid-info" isDesktop={!isMobile}>
        <Attributes
            l1CategorySeoName={adDetails.l1CategorySeoName}
            attributes={adDetails.attributes}
        />

        
        <StickyTabNavigation tabs={tabs} stickyOffset={0} />
        <Overview 
            eleId="1" 
            title="Overview" 
            items={overview}
        />
        <Portfolio eleId="2" />

        {sellerSkills.length > 0 && <VipSkills id="3" title="Services" data={sellerSkills} />}

        {tabs && tabs.some(tab => tab.navigatorIndex.toString() === '4') && (
            <Description
                eleId="4"
                isUI_202506
                isPetsOrPhones={false}
                linesToShow={8}
                showIcon={false}
            />
        )}

        <ReportButton />

        {tabs && tabs.some(tab => tab.navigatorIndex.toString() === '5') && adReviews && adReviews?.length > 0 && (
            <AdReviewsList
                eleId="5"
                isMobile={isMobile}
                total={scoreAndReviews.total}
                average={scoreAndReviews.average}
                topTips={scoreAndReviews.topTips}
                bottomTips={scoreAndReviews.bottomTips}
                adReviews={adReviews}
            />
        )}

        {tabs && tabs.some(tab => tab.navigatorIndex.toString() === '6') && qaPairs && qaPairs.length > 0 && (
            <DetailExpandableList
                eleId="6"
                listTitle="Q&As"
                data={qaPairs}
            />
        )}

        {tabs && tabs.some(tab => tab.navigatorIndex.toString() === '7') && otherServices && otherServices.length > 0 && (
            <DetailExpandableList
                eleId="7"
                listTitle="Other services offered"
                data={otherServices}
            />
        )}

        <RequestAQuoteForm
            isModalOpen={isRequestAQuoteFormOpen}
            requestId={requestId ? requestId.toString() : ''}
            onCloseModal={() => setIsRequestAQuoteFormOpen(false)}
            profile={images[0] ?? ''}
            title={sellerContactDetails.contactName}
            preferredContactType={requestAQuotePreferredContactType}
            sellerId={sellerContactDetails.thirdPartySellerId}
            sellerName={sellerContactDetails.contactName}
            advertId={adDetails.id ? adDetails.id.toString() : ''}
            sellerSkills={sellerSkillsList}
            userId={userData.id ? userData.id.toString() : ''}
            categoryId={adDetails.leafCategoryId ? adDetails.leafCategoryId.toString() : ''}
            currentPageUrl={`https://www.gumtree.com${adDetails.currentPageUrl}`}
        />

        {isPartnerAdDefault1Gam ? (
            <div style={{ width: '100%' }}>
                <AdSlot id={DisplayAdSlotDivNames.partnershipBottom} />
            </div>
        ) : (
            <PartnershipAdSlot
                slotId="partnerAdDefault1"
                {...partnershipParams}
            />
        )}

        <PartnershipAdSlot slotId="partnerAdDefault2" {...partnershipParams} />
        
        <>
            {renderAdSlot(
                DisplayAdSlotDivNames.vipMiddleMobile,
                adDetails.l1CategorySeoName,
                baseConfig.device.type,
                css`
                    margin: 18px 0;
                `
            )}

            {adDetails.showLegalDisclaimer && <Disclaimer />}
            <AdSlot id={DisplayAdSlotDivNames.textLink} />
            <AdSlot id={DisplayAdSlotDivNames.textLinkBase} />
        </>
    </VipAdInfo>
);

export const renderRightPanel = ({
    isMobile,
    adDetails,
    vhcData,
    baseConfig,
    adSource,
    scoreAndReviews,
}: {
    isMobile: boolean;
    adDetails: VipState['adDetails'];
    vhcData: any;
    baseConfig: VipState['baseConfig'];
    adSource?: string;
    scoreAndReviews: {
        average: number;
        total: number;
        topTips: string;
        bottomTips: string;
        type: string;
    };
}) => (
    <RightPanel
        item
        colL={isMobile ? undefined : 6}
        colXl={isMobile ? undefined : 4}
        isDesktop={!isMobile}
    >
        <NewContactCard scoreAndReviews={scoreAndReviews} adSource={adSource} isMobile={isMobile} />
        {renderAdSlot(
            DisplayAdSlotDivNames.mpu,
            adDetails.l1CategorySeoName,
            baseConfig.device.type,
            css`
                margin: 18px 0;
            `
        )}
        <SmallDesktopVhc
            isDesktop={!isMobile}
        >
            {Object.keys(vhcData).length > 0 && (
                <VhcPanelContainer>
                    {vhcData?.checked ? <VHCPanel /> : <VHCPlaceholder />}
                </VhcPanelContainer>
            )}
        </SmallDesktopVhc>

        <SocialPanelRight /** Only Desktop from medium */>
            <VipAdSocial>
                <SharePanel />
            </VipAdSocial>
            <div
                className="vip-ad-carousel"
                {...(baseConfig?.device?.type === 'desktop' && {
                    'data-q': 'stay-safe-desktop',
                })}
            >
                <StaySafe />
            </div>
            {renderAdSlot(
                DisplayAdSlotDivNames.vipMiddleDesktop,
                adDetails.l1CategorySeoName,
                baseConfig.device.type,
                css`
                    margin: 18px 0;
                    width: 100%;
                `
            )}
        </SocialPanelRight>
    </RightPanel>
);

export const renderVipCarouselContainer = ({
    isMobile,
    adDetails,
    mapDetails,
    isPartnerAdCompact1Gam,
    isPartnerAdCompact2Gam,
    partnershipParams,
    isSellerCinchCar,
    breadcrumbs,
    adSource
}: {
    isMobile: boolean;
    adDetails: any;
    mapDetails: any;
    isPartnerAdCompact1Gam: boolean;
    isPartnerAdCompact2Gam: boolean;
    partnershipParams: any;
    isSellerCinchCar: boolean;
    breadcrumbs:any;
    adSource: string;
}) => {
    const partnershipAdSlots =
        isPartnerAdCompact1Gam && isPartnerAdCompact2Gam ? (
            <SlotsWrapper>
                <AdSlot id={DisplayAdSlotDivNames.partnershipWidget} />
                <AdSlot id={DisplayAdSlotDivNames.partnershipWidget1} />
            </SlotsWrapper>
        ) : (
            <CompactPartnershipAdsContainer
                id="partnership-compact-container"
                data-testid="partnership-compact-container"
            >
                <PartnershipAdSlot slotId="partnerAdCompact1" {...partnershipParams} />
                {isPartnerAdCompact2Gam ? (
                    <AdSlot id={DisplayAdSlotDivNames.partnershipWidget1} />
                ) : (
                    <PartnershipAdSlot slotId="partnerAdCompact2" {...partnershipParams} />
                )}
            </CompactPartnershipAdsContainer>
        );

    const adTitleHeaderBlock = (
        <VipAdTitle>
            <HeaderBlock
                hiddeAddress
                {...adDetails}
                visibleOnMap={mapDetails.visibleOnMap}
                isSellerCinchCar={isSellerCinchCar}
            />
        </VipAdTitle>
    );

    return (
        <VipCarouselContainer
            item
            colL={isMobile ? undefined : 6} 
            colXl={isMobile ? undefined : 8}
        >
            <BreadcrumbsWrapper>
                <Breadcrumbs canWrap links={breadcrumbs.links} />
                {adSource !== 'Bark' && <FavouriteButton />}
            </BreadcrumbsWrapper>
            {adTitleHeaderBlock}
            {partnershipAdSlots}
        </VipCarouselContainer>
    );
};

const App: React.FC = () => {
    const {
        adDetails,
        baseConfig,
        userData,
        sellerContactDetails,
        breadcrumbs,
        sellerSkills,
        sellerSkillsList,
        adSource,
        adReviews,
        qaPairs,
        otherServices,
        mapDetails,
        vhcData,
        images,
        isSellerCinchCar,
        tabs,
        overview,
        scoreAndReviews,
        requestId,
    } = useSelector(selectState);
    const {isPartnerAdCompact1Gam, isPartnerAdCompact2Gam, isPartnerAdDefault1Gam} = usePartnershipAdFlags();
    const partnershipParams = useSelector(selectPartnershipParams);
    const isMobile = baseConfig.device?.type === 'mobile';

    const [isRequestAQuoteFormOpen, setIsRequestAQuoteFormOpen] = React.useState(false);
    const [requestAQuotePreferredContactType, setRequestAQuotePreferredContactType] = React.useState<'call' | 'quote'>('quote');

    const openRequestAQuoteForm = (preferredContactType: 'call' | 'quote') => {
        setRequestAQuotePreferredContactType(preferredContactType);
        setIsRequestAQuoteFormOpen(true);
    };

    const ldJsonSchema = getPopulatedSchema(
        adDetails,
        images,
        baseConfig.currentPageUrl,
        baseConfig.domain
    );

    return (
        <QueryClientProvider client={queryClient}>
            <MotorsCTAContext.Provider value={{}}>
                <RequestAQuoteFormContext.Provider value={{ adSource, openRequestAQuoteForm }}>
                    <VipContainer>
                        <VipContent forceShowBreadcrumbs>
                            <StructuredData data={ldJsonSchema} />
                            <Grid item>
                                {
                                    !isMobile &&
                                    <AdBreadcrumbSlot>
                                        <div>
                                            <AdSlot id={DisplayAdSlotDivNames.vipBanner} />
                                        </div>
                                    </AdBreadcrumbSlot>
                                }
                                <AdSlot
                                    id={DisplayAdSlotDivNames.pixel}
                                    css={css`
                                        height: 0;
                                    `}
                                />
                            </Grid>
                            <StickyHeader />
                            {renderVipCarouselContainer({
                                isMobile,
                                adDetails,
                                mapDetails,
                                isPartnerAdCompact1Gam,
                                isPartnerAdCompact2Gam,
                                partnershipParams,
                                isSellerCinchCar,
                                breadcrumbs,
                                adSource,
                            })}
                            {renderRightPanel({
                                isMobile,
                                adDetails,
                                vhcData,
                                baseConfig,
                                adSource,
                                scoreAndReviews,
                            })}
                            <Grid item colL={isMobile ? undefined : 6} colXl={isMobile ? undefined : 8}>
                                {renderVipAdInfo({
                                    isMobile,
                                    partnershipParams,
                                    adDetails,
                                    baseConfig,
                                    userData,
                                    sellerContactDetails,
                                    isPartnerAdDefault1Gam,
                                    sellerSkills: sellerSkills ?? [],
                                    sellerSkillsList,
                                    tabs,
                                    overview,
                                    images,
                                    adSource,
                                    adReviews,
                                    scoreAndReviews,
                                    qaPairs,
                                    otherServices,
                                    requestId,
                                    isRequestAQuoteFormOpen,
                                    setIsRequestAQuoteFormOpen,
                                    requestAQuotePreferredContactType,
                                })}
                            </Grid>
                            <SocialPanelLeft /** Less than medium */ item>
                                <VipAdSocial>
                                    <SharePanel />
                                </VipAdSocial>
                                <StaySafeContainer
                                    {...(baseConfig?.device?.type === 'desktop' ? { 'data-q': 'stay-safe-desktop' } : {})}
                                >
                                    <StaySafe />
                                </StaySafeContainer>
                                <AdSlot
                                    id={DisplayAdSlotDivNames.vipMiddleDesktop}
                                    css={css`
                                        margin: 18px 0;
                                        width: 100%;
                                    `}
                                />
                            </SocialPanelLeft>
                            <div style={{ clear: 'both' }}>
                                <SimilarAdsCarouselContainer>
                                    <SimilarAdsCarousel
                                        seeMoreLink={ breadcrumbs?.links[breadcrumbs.links.length - 1]?.url }
                                    />
                                </SimilarAdsCarouselContainer>
                                {renderAdSlot(
                                    DisplayAdSlotDivNames.fBanner,
                                    adDetails.l1CategorySeoName,
                                    baseConfig.device.type,
                                    css`
                                        padding: 18px 0;
                                        border-top: 1px solid #d8d6d9;
                                        clear: both;
                                    `
                                )}
                                <SeoL1Category />
                            </div>
                        </VipContent>
                    </VipContainer>
                </RequestAQuoteFormContext.Provider>
            </MotorsCTAContext.Provider>
        </QueryClientProvider>
    );
};

App.defaultProps = {
    mapDetails: {},
};

export default App;
